import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Button, Card, Input, Select, BackButton, LoadingSpinner } from '@/components/common';
import { TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import {
  NCB_VEHICLE_CATEGORIES,
  emptyNcbSlab,
  ncbSlabDiff,
  type NcbSlabDraft,
} from './ncbSlabs';
import {
  ADDON_TYPE_OPTIONS,
  emptyAddOn,
  addOnDiff,
  type AddOnDraft,
} from './productAddOns';
import { AssetPickerField } from '@/components/media/AssetPicker';
import type { MediaAsset } from '@/services/assets';
import toast from 'react-hot-toast';

/**
 * Product create/edit -- item 10 of the 2026-09-18 batch.
 *
 * This used to be a `Modal` opened from `ProductsListPage` (see that file's
 * git history / #204's regression test). Moved to its own routed page
 * (`/admin/products/new`, `/admin/products/:id/edit`) so editing gets real
 * back navigation and a URL, per the ask -- and so this form is not squeezed
 * into a modal now that it also carries an Add-Ons editor alongside NCB
 * Slabs. Every piece of the #204 fix travels with it unchanged:
 *
 *   * the editor seeds from the DETAIL payload (fetched here, on this page),
 *     never from a list row -- `ProductListSerializer` sends neither
 *     `description` nor four of the five image FKs, so seeding from it is
 *     seeding a form that believes those fields are already empty;
 *   * a save sends only the fields the user actually changed, diffed against
 *     the snapshot the form was seeded with, so a field this form never
 *     loaded can never be written back blank.
 *
 * If the detail fetch fails, the page shows an error state and never renders
 * the form at all -- a half-seeded editable form is the bug, not a degraded
 * mode to fall back to.
 */

interface Lookup {
  id: string;
  name: string;
}

interface AssetBrief {
  id: string;
  src: string;
  width: number | null;
  height: number | null;
  alt_text: string;
}

interface ProductDetail {
  id: string;
  name: string;
  code: string;
  description?: string;
  insurance_type?: string;
  group?: string;
  subgroup?: string;
  policy_type?: string;
  thumbnail?: string | null;
  banner?: string | null;
  banner_tablet?: string | null;
  banner_mobile?: string | null;
  icon_image?: string | null;
  thumbnail_image?: AssetBrief | null;
  banner_image?: AssetBrief | null;
  banner_tablet_image?: AssetBrief | null;
  banner_mobile_image?: AssetBrief | null;
  icon_image_asset?: AssetBrief | null;
}

const IMAGE_SLOTS = [
  {
    key: 'thumbnail' as const,
    brief: 'thumbnail_image' as const,
    label: 'Card thumbnail',
    hint: 'Used on product cards across public microsites, and as the "logo" shown next to the product name. Around 800x500.',
    preview: 'aspect-[16/10]',
  },
  {
    key: 'icon_image' as const,
    brief: 'icon_image_asset' as const,
    label: 'Icon',
    hint: 'Small mark shown beside the product name. SVG or a transparent PNG.',
    preview: 'aspect-square',
  },
  {
    key: 'banner' as const,
    brief: 'banner_image' as const,
    label: 'Banner (desktop)',
    hint: 'Hero artwork on the product page. Around 1600x600.',
    preview: 'aspect-[16/6]',
  },
  {
    key: 'banner_tablet' as const,
    brief: 'banner_tablet_image' as const,
    label: 'Banner (tablet)',
    hint: 'Optional. Served below 900px.',
    preview: 'aspect-[4/3]',
  },
  {
    key: 'banner_mobile' as const,
    brief: 'banner_mobile_image' as const,
    label: 'Banner (mobile)',
    hint: 'Optional. Served below 560px, so the desktop crop keeps its subject.',
    preview: 'aspect-[3/2]',
  },
];

const IMAGE_KEYS = IMAGE_SLOTS.map((slot) => slot.key);

const initialForm = {
  insurance_type: '',
  group: '',
  subgroup: '',
  policy_type: '',
  name: '',
  description: '',
  thumbnail: null as string | null,
  icon_image: null as string | null,
  banner: null as string | null,
  banner_tablet: null as string | null,
  banner_mobile: null as string | null,
};

type ProductForm = typeof initialForm;

/** The form's view of a product. Every key here is one a save may write. */
const formFromProduct = (product: ProductDetail): ProductForm => ({
  insurance_type: product.insurance_type || '',
  group: product.group || '',
  subgroup: product.subgroup || '',
  policy_type: product.policy_type || '',
  name: product.name || '',
  description: product.description || '',
  thumbnail: product.thumbnail || null,
  icon_image: product.icon_image || null,
  banner: product.banner || null,
  banner_tablet: product.banner_tablet || null,
  banner_mobile: product.banner_mobile || null,
});

/**
 * What an edit should actually send: the difference from the snapshot the form
 * was SEEDED with. See this file's top comment and #204 for why this exists.
 */
function changedFields(current: ProductForm, seed: ProductForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const key of Object.keys(current) as (keyof ProductForm)[]) {
    const next = current[key] ?? '';
    if (next === (seed[key] ?? '')) continue;
    payload[key] = next;
  }
  return payload;
}

export function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const editingId = id ?? null;
  const isEdit = Boolean(editingId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ProductForm>(initialForm);
  const [seedForm, setSeedForm] = useState<ProductForm | null>(null);
  const [editingProduct, setEditingProduct] = useState<ProductDetail | null>(null);
  const [pickedAssets, setPickedAssets] = useState<Record<string, MediaAsset | null>>({});
  const [ncbSlabs, setNcbSlabs] = useState<NcbSlabDraft[]>([]);
  const [originalNcbSlabs, setOriginalNcbSlabs] = useState<NcbSlabDraft[]>([]);
  const [addOns, setAddOns] = useState<AddOnDraft[]>([]);
  const [originalAddOns, setOriginalAddOns] = useState<AddOnDraft[]>([]);

  // The product being edited, fetched on the DETAIL endpoint -- never seeded
  // from a list row. `enabled: isEdit` so a create (`/admin/products/new`)
  // never fires this at all.
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    error: detailErrorObj,
  } = useQuery<ProductDetail>({
    queryKey: ['product-detail-for-edit', editingId],
    queryFn: async () => (await api.get<ProductDetail>(`/products/products/${editingId}/`)).data,
    enabled: isEdit,
    retry: false,
  });

  useEffect(() => {
    if (!detail) return;
    const seed = formFromProduct(detail);
    setEditingProduct(detail);
    setFormData(seed);
    setSeedForm(seed);
  }, [detail]);

  const { data: types = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-types-lookup'],
    queryFn: async () => {
      const res = await api.get('/products/types/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });

  const { data: groups = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-groups-lookup', formData.insurance_type],
    queryFn: async () => {
      const res = await api.get('/products/groups/', {
        params: { insurance_type: formData.insurance_type, page_size: 200 },
      });
      return res.data.results || res.data;
    },
    enabled: !!formData.insurance_type,
  });

  const { data: subgroups = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-subgroups-lookup', formData.group],
    queryFn: async () => {
      const res = await api.get('/products/subgroups/', {
        params: { group: formData.group, page_size: 200 },
      });
      return res.data.results || res.data;
    },
    enabled: !!formData.group,
  });

  const { data: policyTypes = [] } = useQuery<Lookup[]>({
    queryKey: ['policy-types-lookup', formData.insurance_type],
    queryFn: async () => {
      const res = await api.get('/policy-types/', {
        params: { insurance_type: formData.insurance_type, is_active: true, page_size: 200 },
      });
      return res.data.results || res.data;
    },
    enabled: !!formData.insurance_type,
  });

  /** The product's NCB slabs, only once a product exists to attach them to. */
  const { data: loadedNcbSlabs } = useQuery<NcbSlabDraft[]>({
    queryKey: ['product-ncb-slabs', editingId],
    queryFn: async () => {
      const res = await api.get('/products/ncb-slabs/', {
        params: { product: editingId, page_size: 200 },
      });
      const rows = res.data.results || res.data;
      return rows.map((r: Record<string, unknown>) => ({
        id: String(r.id),
        claim_free_years: r.claim_free_years == null ? '' : String(r.claim_free_years),
        ncb_percentage: r.ncb_percentage == null ? '' : String(r.ncb_percentage),
        vehicle_category: (r.vehicle_category as string) || '',
      }));
    },
    enabled: !!editingId,
  });

  useEffect(() => {
    if (!loadedNcbSlabs) return;
    setNcbSlabs(loadedNcbSlabs);
    setOriginalNcbSlabs(loadedNcbSlabs);
  }, [loadedNcbSlabs]);

  /** The product's Add-Ons, same story as NCB Slabs above. */
  const { data: loadedAddOns } = useQuery<AddOnDraft[]>({
    queryKey: ['product-add-ons', editingId],
    queryFn: async () => {
      const res = await api.get('/products/add-ons/', {
        params: { product: editingId, page_size: 200 },
      });
      const rows = res.data.results || res.data;
      return rows.map((r: Record<string, unknown>) => ({
        id: String(r.id),
        name: (r.name as string) || '',
        code: (r.code as string) || '',
        addon_type: (r.addon_type as string) || 'cover',
        is_percentage_based: Boolean(r.is_percentage_based),
        percentage_of_premium: r.percentage_of_premium == null ? '' : String(r.percentage_of_premium),
        fixed_premium: r.fixed_premium == null ? '' : String(r.fixed_premium),
        is_mandatory: Boolean(r.is_mandatory),
      }));
    },
    enabled: !!editingId,
  });

  useEffect(() => {
    if (!loadedAddOns) return;
    setAddOns(loadedAddOns);
    setOriginalAddOns(loadedAddOns);
  }, [loadedAddOns]);

  const syncNcbSlabs = async (productId: string) => {
    const { removed, created, updated } = ncbSlabDiff(ncbSlabs, originalNcbSlabs);
    const body = (s: NcbSlabDraft) => ({
      product: productId,
      claim_free_years: Number(s.claim_free_years),
      ncb_percentage: Number(s.ncb_percentage),
      vehicle_category: s.vehicle_category,
    });
    await Promise.all([
      ...removed.map((s) => api.delete(`/products/ncb-slabs/${s.id}/`)),
      ...created.map((s) => api.post('/products/ncb-slabs/', body(s))),
      ...updated.map((s) => api.patch(`/products/ncb-slabs/${s.id}/`, body(s))),
    ]);
  };

  const syncAddOns = async (productId: string) => {
    const { removed, created, updated } = addOnDiff(addOns, originalAddOns);
    const body = (a: AddOnDraft) => ({
      product: productId,
      name: a.name,
      code: a.code,
      addon_type: a.addon_type,
      is_percentage_based: a.is_percentage_based,
      percentage_of_premium: a.is_percentage_based ? Number(a.percentage_of_premium || 0) : null,
      fixed_premium: a.is_percentage_based ? null : Number(a.fixed_premium || 0),
      is_mandatory: a.is_mandatory,
    });
    await Promise.all([
      ...removed.map((a) => api.delete(`/products/add-ons/${a.id}/`)),
      ...created.map((a) => api.post('/products/add-ons/', body(a))),
      ...updated.map((a) => api.patch(`/products/add-ons/${a.id}/`, body(a))),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: async (data: ProductForm) => {
      let saved;
      if (editingId) {
        if (!seedForm) {
          throw new Error(
            'This product was not fully loaded, so there is nothing safe to compare the changes against. Reload the page.'
          );
        }
        const payload = changedFields(data, seedForm);
        saved = (await api.patch(`/products/products/${editingId}/`, payload)).data;
      } else {
        const payload: Record<string, unknown> = {
          insurance_type: data.insurance_type,
          group: data.group,
          name: data.name,
          description: data.description,
        };
        if (data.subgroup) payload.subgroup = data.subgroup;
        if (data.policy_type) payload.policy_type = data.policy_type;
        for (const key of IMAGE_KEYS) {
          if (data[key]) payload[key] = data[key];
        }
        saved = (await api.post('/products/products/', payload)).data;
      }

      const productId = saved?.id || editingId;
      if (!productId) {
        throw new Error('The product was saved but the server did not return its id, so NCB slabs and add-ons could not be attached.');
      }
      await syncNcbSlabs(productId);
      await syncAddOns(productId);
      return { ...saved, id: productId };
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['ncbSlabs'] });
      queryClient.invalidateQueries({ queryKey: ['product-ncb-slabs'] });
      queryClient.invalidateQueries({ queryKey: ['product-add-ons'] });
      queryClient.invalidateQueries({ queryKey: ['productAddons'] });
      queryClient.invalidateQueries({ queryKey: ['product-detail-for-edit'] });
      toast.success(editingId ? 'Product updated' : 'Product created');
      navigate(`/admin/products/${saved.id}`);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const typeOptions = types.map((t) => ({ value: t.id, label: t.name }));
  const groupOptions = groups.map((g) => ({ value: g.id, label: g.name }));
  const subgroupOptions = subgroups.map((s) => ({ value: s.id, label: s.name }));
  const policyTypeOptions = policyTypes.map((pt) => ({ value: pt.id, label: pt.name }));

  const canSubmit = formData.insurance_type && formData.group && formData.name.trim();

  // Never render the form on a failed detail fetch -- a half-seeded editable
  // form is exactly the #204 bug, and the failure here is worse (this page
  // has no list row to fall back on even accidentally).
  if (isEdit && detailError) {
    return (
      <div className="space-y-6">
        <BackButton to="/admin/products" label="Back to Products" />
        <Card padding="md">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Could not load this product</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400">{getErrorMessage(detailErrorObj)}</p>
        </Card>
      </div>
    );
  }

  if (isEdit && (detailLoading || !editingProduct)) {
    return (
      <div className="space-y-6">
        <BackButton to="/admin/products" label="Back to Products" />
        <div className="flex items-center justify-center py-24">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton to={isEdit ? `/admin/products/${editingId}` : '/admin/products'} label="Back" />
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEdit ? 'Edit Product' : 'Add Product'}
        </h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          saveMutation.mutate(formData);
        }}
        className="space-y-6"
      >
        <Card padding="md">
          <div className="space-y-4">
            <Select
              label="Insurance Type"
              name="insurance_type"
              options={typeOptions}
              value={formData.insurance_type}
              onChange={(e) =>
                setFormData({ ...formData, insurance_type: e.target.value, group: '', subgroup: '', policy_type: '' })
              }
              placeholder="Select an insurance type"
              required
            />
            <Select
              label="Group"
              name="group"
              options={groupOptions}
              value={formData.group}
              onChange={(e) => setFormData({ ...formData, group: e.target.value, subgroup: '' })}
              placeholder={formData.insurance_type ? 'Select a group' : 'Select an insurance type first'}
              disabled={!formData.insurance_type}
              required
            />
            <Select
              label="Subgroup (optional)"
              name="subgroup"
              options={subgroupOptions}
              value={formData.subgroup}
              onChange={(e) => setFormData({ ...formData, subgroup: e.target.value })}
              placeholder={formData.group ? 'Select a subgroup' : 'Select a group first'}
              disabled={!formData.group}
            />
            <Select
              label="Policy Type (optional)"
              name="policy_type"
              options={policyTypeOptions}
              value={formData.policy_type}
              onChange={(e) => setFormData({ ...formData, policy_type: e.target.value })}
              placeholder={formData.insurance_type ? 'Select a policy type' : 'Select an insurance type first'}
              disabled={!formData.insurance_type}
            />
            <Input
              label="Name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              label="Description (optional)"
              name="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
        </Card>

        <Card padding="md">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Images</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Used on public product cards and pages. Each one is served responsively (WebP +
            srcset, with width and height so the layout never jumps). The Card thumbnail also
            doubles as the product's "logo" on its detail page and in list rows.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {IMAGE_SLOTS.map((slot) => {
              const brief = editingProduct?.[slot.brief] || null;
              const stored = formData[slot.key];
              return (
                <AssetPickerField
                  key={slot.key}
                  label={slot.label}
                  hint={slot.hint}
                  previewClassName={slot.preview}
                  asset={pickedAssets[slot.key] || null}
                  fallbackUrl={!pickedAssets[slot.key] && stored && brief ? brief.src : null}
                  onChange={(asset) => {
                    setPickedAssets((current) => ({ ...current, [slot.key]: asset }));
                    setFormData((current) => ({
                      ...current,
                      [slot.key]: asset ? asset.id : null,
                    }));
                  }}
                />
              );
            })}
          </div>
        </Card>

        {/* COUNTRY-8. Slabs used to be reachable only from the NCB Slabs
            screen, which was calling a doubled URL and never loaded -- so in
            practice there was nowhere to maintain them at all. */}
        <Card padding="md">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">NCB Slabs</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            No Claim Bonus by claim-free years. Leave empty if this product does not offer one.
            {!editingId && ' Saved once the product is created.'}
          </p>

          <div className="mt-3 space-y-2">
            {ncbSlabs.map((slab, index) => (
              <div key={slab.id ?? `new-${index}`} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <Input
                    label={index === 0 ? 'Claim-free years' : undefined}
                    type="number"
                    min={0}
                    value={slab.claim_free_years}
                    onChange={(e) =>
                      setNcbSlabs((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, claim_free_years: e.target.value } : r))
                      )
                    }
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    label={index === 0 ? 'NCB %' : undefined}
                    type="number"
                    min={0}
                    max={100}
                    value={slab.ncb_percentage}
                    onChange={(e) =>
                      setNcbSlabs((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, ncb_percentage: e.target.value } : r))
                      )
                    }
                  />
                </div>
                <div className="col-span-5">
                  <Select
                    label={index === 0 ? 'Vehicle type' : undefined}
                    options={NCB_VEHICLE_CATEGORIES}
                    value={slab.vehicle_category}
                    onChange={(e) =>
                      setNcbSlabs((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, vehicle_category: e.target.value } : r))
                      )
                    }
                  />
                </div>
                <div className="col-span-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setNcbSlabs((rows) => rows.filter((_, i) => i !== index))}
                  >
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            type="button"
            className="mt-3"
            onClick={() => setNcbSlabs((rows) => [...rows, emptyNcbSlab()])}
          >
            Add NCB slab
          </Button>
        </Card>

        {/* Item 10: the product create form had no Add-ons field anywhere.
            Same "saved once the product is created" story and the same
            diff-on-save sync as NCB Slabs above -- see `productAddOns.ts`. */}
        <Card padding="md">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Add-Ons</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Riders and additional covers a broker can sell alongside this product.
            {!editingId && ' Saved once the product is created.'}
          </p>

          <div className="mt-3 space-y-3">
            {addOns.map((addOn, index) => (
              <div key={addOn.id ?? `new-addon-${index}`} className="grid grid-cols-12 gap-2 items-end rounded-md border border-gray-200 p-3 dark:border-gray-700">
                <div className="col-span-3">
                  <Input
                    label={index === 0 ? 'Name' : undefined}
                    value={addOn.name}
                    onChange={(e) =>
                      setAddOns((rows) => rows.map((r, i) => (i === index ? { ...r, name: e.target.value } : r)))
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    label={index === 0 ? 'Code' : undefined}
                    value={addOn.code}
                    onChange={(e) =>
                      setAddOns((rows) => rows.map((r, i) => (i === index ? { ...r, code: e.target.value } : r)))
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Select
                    label={index === 0 ? 'Type' : undefined}
                    options={ADDON_TYPE_OPTIONS}
                    value={addOn.addon_type}
                    onChange={(e) =>
                      setAddOns((rows) => rows.map((r, i) => (i === index ? { ...r, addon_type: e.target.value } : r)))
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Select
                    label={index === 0 ? 'Pricing' : undefined}
                    options={[
                      { value: 'percentage', label: '% of premium' },
                      { value: 'flat', label: 'Flat amount' },
                    ]}
                    value={addOn.is_percentage_based ? 'percentage' : 'flat'}
                    onChange={(e) =>
                      setAddOns((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, is_percentage_based: e.target.value === 'percentage' } : r))
                      )
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    label={index === 0 ? (addOn.is_percentage_based ? '% value' : 'Flat value') : undefined}
                    type="number"
                    min={0}
                    value={addOn.is_percentage_based ? addOn.percentage_of_premium : addOn.fixed_premium}
                    onChange={(e) =>
                      setAddOns((rows) =>
                        rows.map((r, i) =>
                          i === index
                            ? r.is_percentage_based
                              ? { ...r, percentage_of_premium: e.target.value }
                              : { ...r, fixed_premium: e.target.value }
                            : r
                        )
                      )
                    }
                  />
                </div>
                <div className="col-span-11 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`addon-mandatory-${index}`}
                    checked={addOn.is_mandatory}
                    onChange={(e) =>
                      setAddOns((rows) => rows.map((r, i) => (i === index ? { ...r, is_mandatory: e.target.checked } : r)))
                    }
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor={`addon-mandatory-${index}`} className="text-sm text-gray-700 dark:text-gray-300">
                    Mandatory
                  </label>
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setAddOns((rows) => rows.filter((_, i) => i !== index))}
                  >
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            type="button"
            className="mt-3"
            onClick={() => setAddOns((rows) => [...rows, emptyAddOn()])}
          >
            Add Add-On
          </Button>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate(isEdit ? `/admin/products/${editingId}` : '/admin/products')}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending} disabled={!canSubmit}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ProductFormPage;
